import React from 'react';
import { AttributeTilePicker } from '@/ui/components/pages/ProductDetail/attribute-tile-picker/attribute-tile-picker';
import { ColorPicker } from '@/ui/components/pages/ProductDetail/color-picker/color-picker';
import { AttributeTileDetail } from '@/ui/components/pages/ProductDetail/types/attribute-tile-detail';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';
import { PickerSize } from '@/ui/components/pages/ProductDetail/types/picker-size';
import { ParsedHardwareProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { BundleProduct } from '@/ui/services/BundleProductService';

export interface BundleOptionPickerProps {
  product: BundleProduct;
  selectedSku?: string;
  onSelect: (variant: ParsedHardwareProductVariant) => void;
  size?: PickerSize;
}

/**
 * Picker for one bundle slot: colour swatches when the slot's variants differ by
 * colour, variant tiles otherwise, nothing when the slot has a single variant.
 * Takes the selected SKU and reports the chosen variant, whatever the slot shows.
 * Options the visitor's country hides are left out, as on the product's own page.
 */
export function BundleOptionPicker({
  product,
  selectedSku,
  onSelect,
  size = 'default',
}: Readonly<BundleOptionPickerProps>) {
  const { country } = useLocalization();
  const options = product.getSlotOptions(country);

  const selectOption = (option: ColorDetail | AttributeTileDetail) => {
    const chosen = product.getVariantForOption(option);
    if (chosen) {
      onSelect(chosen);
    }
  };

  if (options.kind === 'color') {
    const selectedColor = selectedSku ? product.getVariantBySKU(selectedSku)?.color : undefined;

    return (
      <ColorPicker
        availableColors={options.colors}
        selectedColor={selectedColor}
        setSelectedColor={(color) => selectOption(color)}
        size={size}
      />
    );
  }

  if (options.kind === 'variant') {
    return (
      <AttributeTilePicker
        options={options.tiles}
        selected={options.tiles.find((tile) => tile.type === selectedSku)}
        onSelect={(tile) => selectOption(tile)}
        size={size}
      />
    );
  }

  return null;
}
